'use client'

import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { createLogger } from '@/lib/logger'

const logger = createLogger('SegmentPlayer')

export type SegmentPlayRequest = {
  youtubeId: string
  startSeconds: number
  endSeconds?: number
  segmentName: string
  segmentId: number
  videoName: string
  videoId: number
  // Thumbnail source data for the minimized floating player, resolved via
  // getThumbnail() at render time so the override > youtube/mux > local
  // priority stays canonical instead of a producer baking in a single URL.
  thumbnail?: { id: number; url: string } | null
  thumbnailOverride?: { id: number; url: string } | null
  // Ties this request to a VideoPlayer instance's registered anchor (see
  // registerAnchor below) so SegmentPlayer can render inline there instead of
  // the floating popout, and hand off to the popout when that anchor scrolls
  // out of view. Omitted by VideoSegmentChip/VideoAppChip - those always play
  // in the popout since they have no on-page video element to render into.
  playerKey?: string
}

export type SegmentPlayerApi = {
  play: (request: SegmentPlayRequest) => void
  // Registers (element non-null) or unregisters (element null) the DOM node a
  // VideoPlayer instance wants playback portaled into when it owns the active
  // segment. Unregistering the active key (route change, modal close, etc.)
  // does NOT stop playback: the entry is removed and the player falls back to
  // the floating popout, preserving seamless continuation across navigations.
  registerAnchor: (key: string, element: HTMLElement | null) => void
  // The playerKey of whichever request is currently active, so a VideoPlayer
  // instance can tell it owns the live player rather than showing its own
  // placeholder.
  activePlayerKey: string | null
}

const defaultApi: SegmentPlayerApi = {
  play: (request) => {
    // No real player is mounted. Chips still render and report intent so the
    // SegmentPlayer (EA-738) can be a drop-in replacement without further wiring.
    logger.warn('SegmentPlayer not mounted - play() ignored', {
      youtubeId: request.youtubeId,
      segmentId: request.segmentId,
      startSeconds: request.startSeconds,
      endSeconds: request.endSeconds,
    })
  },
  registerAnchor: () => {},
  activePlayerKey: null,
}

const SegmentPlayerContext = createContext<SegmentPlayerApi>(defaultApi)

export function useSegmentPlayer(): SegmentPlayerApi {
  return useContext(SegmentPlayerContext)
}

export function SegmentPlayerProvider({
  api,
  children,
}: {
  api: SegmentPlayerApi
  children: ReactNode
}) {
  // Stabilize the value to avoid spurious re-renders of consumers when callers
  // construct `api` inline.
  const value = useMemo<SegmentPlayerApi>(
    () => ({
      play: api.play,
      registerAnchor: api.registerAnchor,
      activePlayerKey: api.activePlayerKey,
    }),
    [api.play, api.registerAnchor, api.activePlayerKey],
  )
  return <SegmentPlayerContext.Provider value={value}>{children}</SegmentPlayerContext.Provider>
}
